import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, Chip, Divider,
    Alert, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditIcon from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

const ForceCompleteTripDialog = ({ open, onClose, onConfirm, trip, isProcessing }) => {
    const [formData, setFormData] = useState({
        startTime: null,
        endTime: null,
        reason: ''
    });

    // Initialize form data when dialog opens
    useEffect(() => {
        if (open && trip) {
            const plannedStart = trip.plannedStartTime ? moment(trip.plannedStartTime) : moment();
            const currentTime = moment();

            setFormData({
                startTime: plannedStart,
                endTime: currentTime,
                reason: ''
            });
        }
    }, [open, trip]);

    const handleConfirm = () => {
        if (trip && formData.reason && formData.startTime && formData.endTime) {
            const payload = {
                startTime: formData.startTime.format('YYYY-MM-DDTHH:mm:ss'),
                endTime: formData.endTime.format('YYYY-MM-DDTHH:mm:ss'),
                reason: formData.reason
            };
            onConfirm(payload);
            setFormData({ startTime: null, endTime: null, reason: '' });
        }
    };

    const handleClose = () => {
        setFormData({ startTime: null, endTime: null, reason: '' });
        onClose();
    };

    const getIssueStrings = () => {
        if (!trip) return [];

        const issues = [];
        if (trip.issues?.issueString && trip.issues.issueString.length > 0) {
            issues.push(...trip.issues.issueString);
        }

        // Check for violations
        if (trip.violationsCount > 0) {
            issues.push(`${trip.violationsCount} rule violation(s) detected`);
        }

        // Check for route violations
        if (trip.significantEvents?.some(event => event.eventName === 'Route Violation')) {
            issues.push('Route deviation detected');
        }

        // Check for delays
        if (trip.tripStage === 'Start Delayed') {
            issues.push('Trip start delayed');
        }

        // Check for incomplete trips
        if (trip.completionPercentage < 100) {
            issues.push(`Trip only ${Math.round(trip.completionPercentage)}% complete`);
        }

        // Check for halted status
        if (trip.activeStatus?.toLowerCase().includes('halted')) {
            issues.push('Vehicle currently halted');
        }

        // Check for detention
        if (trip.activeStatus?.toLowerCase().includes('detained')) {
            issues.push('Vehicle detained at location');
        }


        return issues;
    };

    const issueStrings = getIssueStrings();

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 3,
                    px: 4
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WarningIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Force Complete Trip
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Issue Strings Section */}
                {issueStrings.length > 0 && (
                    <Box sx={{ p: 3, backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#dc2626' }}>
                            Issues Detected:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {issueStrings.map((issue, index) => (
                                <Chip
                                    key={index}
                                    label={issue}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        border: '1px solid #fecaca',
                                        fontWeight: 500,
                                        '&:hover': {
                                            backgroundColor: '#fecaca'
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Trip Info Section */}
                {trip && (
                    <Box sx={{ p: 3, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Trip Details
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                Vehicle:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {trip.truckRegistrationNumber}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                Route:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {trip.route?.routeName?.split('|')[0] || 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Form Section */}
                <Box sx={{ p: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Start Time */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ScheduleIcon sx={{ color: 'primary.main' }} />
                                    Trip Start Time
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <DatePicker
                                        label="Start Date"
                                        value={formData.startTime}
                                        onChange={(value) => setFormData(prev => ({ ...prev, startTime: value }))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                variant="outlined"
                                                size="medium"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#f8fafc',
                                                        '&:hover': {
                                                            backgroundColor: '#f1f5f9'
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <TimePicker
                                        label="Start Time"
                                        value={formData.startTime}
                                        onChange={(value) => setFormData(prev => ({ ...prev, startTime: value }))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                variant="outlined"
                                                size="medium"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#f8fafc',
                                                        '&:hover': {
                                                            backgroundColor: '#f1f5f9'
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>

                            {/* End Time */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EditIcon sx={{ color: 'primary.main' }} />
                                    Trip End Time
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <DatePicker
                                        label="End Date"
                                        value={formData.endTime}
                                        onChange={(value) => setFormData(prev => ({ ...prev, endTime: value }))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                variant="outlined"
                                                size="medium"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#f8fafc',
                                                        '&:hover': {
                                                            backgroundColor: '#f1f5f9'
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <TimePicker
                                        label="End Time"
                                        value={formData.endTime}
                                        onChange={(value) => setFormData(prev => ({ ...prev, endTime: value }))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                variant="outlined"
                                                size="medium"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#f8fafc',
                                                        '&:hover': {
                                                            backgroundColor: '#f1f5f9'
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>

                            {/* Reason */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                    Reason for Force Completion
                                </Typography>
                                <TextField
                                    multiline
                                    rows={3}
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Please provide a detailed reason for force completing this trip..."
                                    value={formData.reason}
                                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: '#f8fafc',
                                            '&:hover': {
                                                backgroundColor: '#f1f5f9'
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </LocalizationProvider>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f8fafc', gap: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        borderColor: '#cbd5e1',
                        color: '#64748b',
                        '&:hover': {
                            borderColor: '#94a3b8',
                            backgroundColor: '#f1f5f9'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!formData.reason || !formData.startTime || !formData.endTime || isProcessing}
                    variant="contained"
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #ff5252 0%, #ff5722 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 8px 25px rgba(255, 107, 107, 0.3)'
                        },
                        '&:disabled': {
                            background: '#e2e8f0',
                            color: '#94a3b8'
                        }
                    }}
                >
                    {isProcessing ? 'Processing...' : 'Force Complete Trip'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ForceCompleteTripDialog; 